<template>
  <div v-if="isMobile" class="login-header-wrap">
    <KHeaderBar>
      <img
        class="login-header-logo"
        :src="$cvWebpFn('https://image.kilimall.com/c/h5/login_logo.png', { w: 240 })"
        alt="Kilimall"
      >
    </KHeaderBar>
  </div>
  <div :class="isMobile ? 'login-wrapper' : 'login-wrapper pc__login-wrapper'">
    <div :class="!isMobile && 'pc__login-inner-wrapper'">
      <div v-if="!isMobile" class="pc-login-logo-wrap">
        <img
          class="login-header-logo pc-login-logo"
          :src="$cvWebpFn('https://image.kilimall.com/c/h5/login_logo.png', { w: 320 })"
          alt="Kilimall"
        >
      </div>
      <div class="social-login-section">
        <div class="other-login-btn google" @click="handleGoogleLoginClick">
          <img v-lazy="$cvWebpFn('https://img.kilimall.com/kenya/kilimall_lite/ic_google_2x.png')" alt="">
          <span>Continue with Google</span>
        </div>
        <div class="other-login-btn facebook" @click="handleFacebookLogin">
          <img v-lazy="$cvWebpFn('https://img.kilimall.com/kenya/kilimall_lite/ic_facebook_2x.png')" alt="">
          <span>Continue with Facebook</span>
        </div>
      </div>

      <div class="or-divider">
        <span class="or-line"></span>
        <span class="or-text">Or</span>
        <span class="or-line"></span>
      </div>

      <div v-show="loginType === 1">
        <LoginWithPassword
          submit-text="Login"
          @switch:logintype="loginType = 2"
        />
      </div>
      <div v-show="loginType === 2">
        <LoginWithCode
          ref="withCodeObj"
          submit-text="Continue/Register"
        />
      </div>

      <p class="switch-login-fn">
        <span v-if="loginType === 1" @click="loginType = 2">
          <span class="switch-label-log ">Log in with SMS</span>
        </span>
        <span v-else @click="loginType = 1">
          <span class="switch-label-log">Log in with Password</span>
        </span>
      </p>

      <div class="footer-register">
        <p class="footer-register-new">New to Kilimall? <span @click="loginType = 2" class="switch-label">Register</span></p>
        <div class="policy-desc">
          By continuing, you agree to Kilimall's
          <p><a href="/help-center/oh_3467?c_id=1083" target="_blank">Terms & Conditions <label>and</label> Privacy Policy</a></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type ILoginFacebook,
  queryGoogleIsNew,
  queryFacebookIsNew
} from '@/api/models/system'
import { showToast } from 'vant'
import { useRouter } from 'vue-router'
import { watch } from 'vue'
import { tabPageViewFn } from '@/plugins/bi-sa.client'
import { webClickFn } from '@/plugins/bi-sa.client'

const router = useRouter()
const isMobile = useIsMobile().value
const { 
  $GAuth,
  $asyncLoadJs,
  $googleLogin,
  $facebookLogin
} = useNuxtApp()
definePageMeta({ layout: 'wap' })

// 1: pwd 2: code
const loginType = ref(1)
const runtimeConfig = useRuntimeConfig()
const withCodeObj = ref()
const handleGoogleLoginClick = async () => {
  let webClickIndex = 10
  if (loginType.value === 2) {
    webClickIndex = 11
    if(withCodeObj.value?.tabAct === 'email') {
      webClickIndex = 12
    } else if(withCodeObj.value?.tabAct === 'whatsapp') {
      webClickIndex = 14
    }
  }
  const googleUser = await $GAuth.$gAuth.signIn()
  const id = googleUser.getId()
  const idToken = googleUser.getAuthResponse().id_token  

  try {
    const { isNewUser } = await queryGoogleIsNew(id)
    if(isNewUser) {
      const strings = idToken.split(".") //截取token，获取载体
      let isEmail = 0
      const userinfo = JSON.parse(decodeURIComponent(escape(window.atob(strings[1].replace(/-/g, "+").replace(/_/g, "/"))))) //解析，需要吧‘_’,'-'进行转换否则会无法解析

      isEmail = userinfo.email ? 1 : 0
      webClickFn(webClickIndex, 'Continue with Google', true)
      router.push(`/quick-reg-binding?id=${id}&idToken=${idToken}&type=google&email=${isEmail}`)
    } else {
      await $googleLogin(idToken)
      webClickFn(webClickIndex, 'Continue with Google', true)
    }
  } catch(e: any) {
    console.error(e)
    showToast(e.msg || e.message)
    webClickFn(webClickIndex, 'Continue with Google', false, 'api_code_error', e.msg || e.message)
  }
}

let facebookInitialized = false
const handleFacebookLogin = async() => {
  try {
    const initializeFacebook = () => {
      if (facebookInitialized || !window.FB) return
      FB.init({
        appId: runtimeConfig.public.fbAppId,
        cookie: false, // enable cookies to allow the server to access
        // the session,
        // autoLogAppEvents: true,
        xfbml: true, // parse social plugins on this page
        version: 'v18.0' // The Graph API version to use for the call
      })
      facebookInitialized = true
    }
    // Register before insertion: a cached script can execute before load resolves.
    window.fbAsyncInit = initializeFacebook
    await $asyncLoadJs('https://connect.facebook.net/en_US/sdk.js', {
      id: 'facebook-jssdk', ready: () => !!window.FB?.getLoginStatus
    })
    initializeFacebook()
    FB.getLoginStatus(statusChangeCallback)
  } catch {
    showToast('Unable to load Facebook login. Please try again.')
  }
}
const statusChangeCallback = async(res: any) => {
  const fbLogin = (): Promise<any> => new Promise(resolve => FB.login(resolve))
  let webClickIndex = 7
  if (loginType.value === 2) {
    webClickIndex = 8
    if(withCodeObj.value?.tabAct === 'email') {
      webClickIndex = 9
    } else if(withCodeObj.value?.tabAct === 'whatsapp') {
      webClickIndex = 15
    }
  }
  if(res.status !== 'connected') {
    res = await fbLogin()
  }

  if(res.status === 'connected') {
    await loginSuccessCallback(res.authResponse, webClickIndex)
  } else {
    console.error('facebook login fail !!!')
    webClickFn(webClickIndex, 'Continue with Facebook', false, 'api_code_error', 'facebook login fail')
  }
}

const loginSuccessCallback = async (loginRes: any, webClickIndex: number) => {
  FB.api('/me', async(meRes: any) => {
    const { userID, accessToken } = loginRes
    const name = meRes.name
    
    try {
      const { isNewUser } = await queryFacebookIsNew(userID)
      if(isNewUser) {
        router.push(`/quick-reg-binding?fbUserId=${userID}&fbUserName=${name}&fbAccessToken=${accessToken}&type=facebook`)
      } else {
        const loginParams: ILoginFacebook = {
          fbUserId: userID,
          fbUserName: name,
          fbAccessToken: accessToken
        }

        await $facebookLogin(loginParams)
        webClickFn(webClickIndex, 'Continue with Facebook', true)
      }
    } catch(e: any) {
      console.error('facebook login fail: ', e)
      webClickFn(webClickIndex, 'Continue with Facebook', false, 'api_code_error', 'facebook login fail')
    }
  })
}
watch(() => loginType.value, (val) => {
  if (val === 2) {
    // tab切换埋点
    tabPageViewFn('loginSignUpEmailOrSms')
  }
})
</script>

<style lang="scss" scoped>
.login-wrapper{
  text-align: center;
  background-color: #fff;
  padding-bottom: 60px;
}
.login-header-wrap{
  :deep(.van-nav-bar)::after,
  :deep(.van-hairline--bottom)::after {
    display: none;
  }
  :deep(.van-nav-bar) {
    border-bottom: none;
  }
}
.login-header-logo{
  height: 48px;
  width: auto;
  object-fit: contain;
  display: block;
}
.pc-login-logo-wrap{
  display: flex;
  justify-content: center;
  padding: 20px 0 10px;
}
.pc-login-logo{
  height: 64px;
}
.social-login-section{
  margin-top: 145px;
  padding: 0 40px;
}
.or-divider{
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 40px 40px 20px;
  .or-line{
    flex: 1;
    height: 1px;
    background: #E5E5E5;
  }
  .or-text{
    padding: 0 24px;
    color: #999;
    font-size: 26px;
  }
}
.policy-desc{
  font-size: 24px;
  color: #999;
  margin-top: 24px;
  line-height: 36px;
  padding: 0 40px;
  span{
    border-bottom: 1px solid #999;
  }
  a{
    color: #999;
    font-weight: 700;
    font-size: 24px;
    label {
      font-weight: 400;
    }
  }
}
.footer-register{
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  width: 100%;
  text-align: center;
  font-size: 28px;
  color: #505050;
  .footer-register-new {
    font-size: 28px;
    color: #999999;
    margin-bottom: 50px;
  }
}
.pc__login-wrapper{
  height: 100vh;
  @include center();
  .footer-register{
    position: static;
  }
  .social-login-section{
    margin-top: calc(145px - var(--van-nav-bar-height));
  }
}
.login-wrapper :deep(.van-cell) {
  padding: 20px 0;
  &::after{
    border-bottom: 1px solid #999999;
    left: 0;
    right: 0;
  }
}
.login-wrapper :deep(.van-field__control::placeholder) {
  color: #999999;
}
.login-wrapper :deep(.van-field__control::-webkit-input-placeholder) {
  color: #999999;
}
.login-wrapper :deep(.van-field__control::-moz-placeholder) {
  color: #999999;
}
.pc__login-inner-wrapper{
  width: 1000px;
  padding: 30px;
  box-shadow: 0 0 20px #ddd;
}
.switch-login-fn{
  margin: 40px 0 30px;
  font-size: 28px;
  color: var(--font_color_05);
}
.switch-label{
  color: #6786FD;
  cursor: pointer;
  font-size: 28px;
  font-weight: 700;
  text-decoration-line: underline;
}
.switch-label-log {
  cursor: pointer;
  font-size: 28px;
  font-weight: 700;
  color: #150000;
}
.other-login-btn{
  position: relative;
  display: flex;
  cursor: pointer;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  border-radius: 12px;
  height: 88px;
  width: 100%;
  margin-bottom: 30px;
  border: 2px solid #E0E0E0;
  font-weight: 700;
  background: #fff;
  img{
    position: absolute;
    left: 32px;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
  span{
    text-align: center;
  }
}
.google{
  color: #333;
}
.facebook{
  color: #333;
}
</style>
